import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { IonIcon, IonRouterLink } from '@ionic/react';
import { t } from 'i18next';
import { chatbubbleOutline, chevronForward, golfOutline } from 'ionicons/icons';

type Friend = {
  id: number;
  userId: string;
  firstName: string | null;
  lastName: string | null;
  username: string;
  imageUrl: string | null;
  handicap: number | null;
};

type FriendsListProps = {
  friends: Friend[];
};

export default function FriendsList({ friends }: FriendsListProps) {
  return (
    <div className="space-y-4">
      {friends.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
          <div className="rounded-full bg-secondary p-3 mb-4">
            <IonIcon icon={golfOutline} className="size-6 text-primary" />
          </div>
          <h3 className="text-lg font-medium mb-2">{t('no_friends_yet')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('find_golfers_to_connect')}
          </p>
        </div>
      ) : (
        <div className="md:flex grid grid-cols-1 md:flex-wrap gap-3 p-2">
          {friends.map(friend => (
            <FriendCard key={friend.id} friend={friend} />
          ))}
        </div>
      )}
    </div>
  );
}

type FriendCardProps = {
  friend: Friend;
};

function FriendCard({ friend }: FriendCardProps) {
  return (
    <div className="relative overflow-hidden rounded-lg border bg-card shadow-sm md:w-[calc(50%-0.5rem)] w-full">
      <IonRouterLink
        routerLink={`/u/${friend.username}`}
        className="block p-4 ion-activatable"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Avatar className="size-14 border-2 border-primary/10">
              <AvatarImage src={friend.imageUrl || undefined} />
              <AvatarFallback className="bg-primary/5 text-primary">
                {friend.firstName?.[0]}
                {friend.lastName?.[0]}
              </AvatarFallback>
            </Avatar>

            <div>
              <h3 className="font-medium">
                {friend.firstName} {friend.lastName}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-muted-foreground">@{friend.username}</span>
                {friend.handicap !== null && (
                  <span className="inline-flex items-center rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                    HCP {friend.handicap}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center">
            <IonIcon icon={chevronForward} className="text-muted-foreground" />
          </div>
        </div>
      </IonRouterLink>
    </div>
  );
}
